import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../api/auth';
import { toFormErrors, type FormErrors } from '../auth/errors';
import { Alert, Button, Input } from '../components/ui';
import { AuthLayout } from './AuthLayout';

const NO_ERRORS: FormErrors = { fields: {}, message: null };

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors(NO_ERRORS);
    setIsSubmitting(true);

    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (error) {
      setErrors(toFormErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="That link is incomplete">
        <Alert tone="danger" title="Missing token">
          This reset link is missing its token. Ask for a new one from{' '}
          <Link to="/forgot-password">the reset page</Link>.
        </Alert>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="Every other signed-in device was signed out.">
        <Alert tone="success" title="All set">
          <Link to="/login">Sign in with your new password</Link>.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form className="sc-form" onSubmit={handleSubmit} noValidate>
        {errors.message && <Alert tone="danger">{errors.message}</Alert>}

        <Input
          label="New password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          hint="At least 10 characters, and not too close to your name or email."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={errors.fields['password']}
        />

        <div className="sc-form__actions">
          <Button type="submit" isLoading={isSubmitting} loadingLabel="Updating password" fullWidth>
            Update password
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
